import { BaseLookupDto } from "@api-types/lookups/BaseLookupDto";

import {
	LanguagesLookupModel,
	TitlesLookupModel,
	UserAccountLockDurationTypesModel,
	UserAccountLockReasonTypesModel,
	OasisLockReasonsTypesModel,
} from "@api-types/LookupsDto";

import { httpClient, getWebApiUrl } from "@utils";
import { BasicLookupModel } from "@api-types";
import { LimitTypeItem, Lookup, TLimitDurationItem } from "@data-types";
import { CountryDataType } from "@data-types/membership/registration";

import { ContextOptions, ApiRequestContextData } from "@utils/api-config/types";
import { getMvcRequestHeaders } from "@utils/api-config/getMvcRequestHeaders";
import { getApiRequestContext } from "@utils/api-config";
import { getApplicationRequestHeaders } from "@utils/api-config/getApplicationRequestHeader";
import { createApiRequestOptions } from "@utils/api-config/createApiRequestOptions";
import { RequestContextType } from "@utils/api-config/RequestContext";

export default class LookupApiService {
	static getRequestHeaders = (
		context: ApiRequestContextData | null
	): HeadersInit => {
		const appHeaders = getApplicationRequestHeaders(context);
		appHeaders["Content-Type" as keyof HeadersInit] = "application/json";
		return appHeaders;
	};

	static getMvcRequestHeaders = (
		context: ApiRequestContextData | null
	): HeadersInit => {
		const appHeaders = getMvcRequestHeaders(context);
		appHeaders["Content-Type" as keyof HeadersInit] = "application/json";
		return appHeaders;
	};

	static async fetchGamingLimitDurationsLookup(): Promise<
		Lookup<TLimitDurationItem>
	> {
		const context = getApiRequestContext();

		const url = `${WEBPACK_BASE_ADDRESS}${context?.agencyKey}/gaming-limit-durations?translate=true`;
		return httpClient.get(url, this.getRequestHeaders(context));
	}

	static async fetchGamingLimitCategoriesLookup(
		options: ContextOptions
	): Promise<Lookup<LimitTypeItem>> {
		const context = getApiRequestContext(options);
		const url = `${WEBPACK_BASE_ADDRESS}${context?.agencyKey}/gaming-limit-categories?translate=true`;
		return httpClient.get(url, this.getRequestHeaders(context));
	}

	static async fetchCountriesLookup(): Promise<Lookup<CountryDataType>> {
		const context = getApiRequestContext();
		const url = `${WEBPACK_BASE_ADDRESS}platform/countries?sort=name|asc&rpp=1000&translate=true`;
		return httpClient.get(url, this.getRequestHeaders(context));
	}

	static async fetchLanguagesLookupByIso(
		isos: string
	): Promise<Lookup<LanguagesLookupModel>> {
		const context = getApiRequestContext();
		const url = `${WEBPACK_BASE_ADDRESS}platform/languages?sort=name|asc&rpp=1000&translate=true&isos=${isos}`;

		return await httpClient.get(url, this.getRequestHeaders(context));
	}

	static async fetchLanguagesLookup(): Promise<Lookup<LanguagesLookupModel>> {
		const context = getApiRequestContext();

		const url = `${WEBPACK_BASE_ADDRESS}platform/languages?sort=name|asc&rpp=1000&translate=true`;
		return httpClient.get(url, this.getRequestHeaders(context));
	}

	static async fetchTitlesLookup(): Promise<Lookup<TitlesLookupModel>> {
		const context = getApiRequestContext();

		const url = `${WEBPACK_BASE_ADDRESS}platform/user-titles?translate=true`;
		return httpClient.get(url, this.getRequestHeaders(context));
	}

	static async fetchUserAccountLockDurationTypes(): Promise<
		Lookup<UserAccountLockDurationTypesModel>
	> {
		const context = getApiRequestContext();

		const url = `${WEBPACK_BASE_ADDRESS}platform/platform-user-account-lock-duration-types?fields=id,abrv,name,units,languageMetadata&translate=true&rpp=20`;
		return httpClient.get(url, this.getRequestHeaders(context));
	}

	static async fetchUserAccountLockReasonTypes(): Promise<
		Lookup<UserAccountLockReasonTypesModel>
	> {
		const context = getApiRequestContext();

		const url = `${WEBPACK_BASE_ADDRESS}platform/user-account-lock-reason-types?fields=id,abrv,name,languageMetadata&translate=true&rpp=20`;
		return httpClient.get(url, this.getRequestHeaders(context));
	}

	static async fetchOasisLockReasonsTypes(): Promise<
		Lookup<OasisLockReasonsTypesModel>
	> {
		const context = getApiRequestContext();

		const url = `${WEBPACK_BASE_ADDRESS}platform/block-reasons?fields=abrv,code,id,name,languageMetadata&translate=true`;
		return httpClient.get(url, this.getRequestHeaders(context));
	}

	static async fetchGamingLimitTypes() {
		const context = getApiRequestContext();

		const url = `${WEBPACK_BASE_ADDRESS}${context?.agencyKey}/agency-gaming-limit-types?translate=true`;
		return httpClient.get(url, this.getRequestHeaders(context));
	}

	static async getAccountStatementTypes(
		options: ContextOptions
	): Promise<BaseLookupDto[] | void> {
		const context = this.getApiRequestContext(options);
		const headers = this.getRequestHeaders(context);

		const url = `${WEBPACK_BASE_ADDRESS}${context?.agencyKey}/transaction-types/account-statement-types?bettingAccountTypeIds=${context?.accountTypeId}&translate=true`;
		return httpClient.get(url, headers);
	}

	static async getAutoLogoutPeriods(): Promise<{
		// TODO this is extracted from existing code, confirm this is actual response
		item: { units: number; id: string; abrv: string; name: string }[];
	}> {
		const context = getApiRequestContext();

		const url = `${WEBPACK_BASE_ADDRESS}platform/auto-logout-periods?fields=abrv,name,id,units,languageMetadata&rpp=20&translate=true`;
		return httpClient.get(url, this.getRequestHeaders(context));
	}

	static async getBettingTimeouts(): Promise<{
		// TODO this is extracted from existing code, confirm this is actual response
		item: { units: number; id: string; abrv: string; name: string }[];
	}> {
		const context = getApiRequestContext();

		const url = `${WEBPACK_BASE_ADDRESS}platform/betting-timeouts?fields=abrv,units,id,name,languageMetadata&rpp=20&translate=true`;
		return httpClient.get(url, this.getRequestHeaders(context));
	}

	static async getUserPaymentMethods(
		isDeposit: boolean,
		options = createApiRequestOptions(RequestContextType.Online)
	) {
		const context = this.getApiRequestContext(options);

		const url = `${WEBPACK_BASE_ADDRESS}platform/payment-methods/enabled-per-payment-mode/${isDeposit}?translate=true`;
		return httpClient.get(url, this.getRequestHeaders(context));
	}

	static async getTransactionStatuses(
		options = createApiRequestOptions(RequestContextType.Online)
	) {
		const context = this.getApiRequestContext(options);

		const url = `${WEBPACK_BASE_ADDRESS}${context?.agencyKey}/transaction-status/user-status?translate=true`;
		return httpClient.get(url, this.getRequestHeaders(context));
	}

	static getApiRequestContext(options: ContextOptions) {
		return getApiRequestContext(options);
	}

	static findLookup(
		type: string,
		options: ContextOptions
	): Promise<BasicLookupModel[]> {
		const context = this.getApiRequestContext(options);
		const headers = this.getMvcRequestHeaders(context);

		const url = `${getWebApiUrl()}/api/lookups/${type}`;
		return httpClient.get(url, headers);
	}
}
